import classNames from 'classnames';
import { useRef, useState } from 'react';
import { usePopper } from 'react-popper';
import { Portal } from 'react-portal';

import { Transition } from '@headlessui/react';

export interface InfoTooltipProps {
  className?: string;
  referenceClassName?: string;
  message?: React.ReactNode;
  messageClassName?: string;
  inline?: boolean;
  enterDelay?: string;
  leaveDelay?: string;
  disabled?: boolean;
}

/**
 * Simple tooltip
 */
export function InfoTooltip({
  children,
  className,
  referenceClassName,
  message,
  messageClassName,
  inline = true,
  enterDelay,
  leaveDelay,
  disabled = false,
}: React.PropsWithChildren<InfoTooltipProps>) {
  const [open, setOpen] = useState(false);

  const popperElRef = useRef(null);
  const [referenceElement, setReferenceElement] = useState(null);
  const [popperElement, setPopperElement] = useState(null);

  const { styles, attributes } = usePopper(referenceElement, popperElement, {
    placement: 'bottom',
    modifiers: [
      {
        name: 'offset',
        options: {
          offset: [0, 8],
        },
      },
      {
        name: 'preventOverflow',
        options: {
          boundary: 'clippingParents',
        },
      },
      {
        name: 'flip',
        options: {
          allowedAutoPlacements: ['bottom'],
          fallbackPlacements: ['bottom', 'top'],
          altBoundary: true,
        },
      },
    ],
  });

  if (disabled) {
    return (
      // eslint-disable-next-line react/jsx-no-useless-fragment
      <>{children}</>
    );
  }

  return (
    <div className={classNames('relative', inline ? 'inline-flex' : 'flex', className)}>
      <div
        className={classNames('relative flex-1 cursor-pointer text-secondary hover:text-gray-500', referenceClassName)}
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        ref={setReferenceElement as any}
        onMouseEnter={() => setOpen(true)}
        onMouseLeave={() => setOpen(false)}
      >
        {children}
      </div>
      <Portal>
        <div ref={popperElRef} style={styles['popper']} className="z-40" {...attributes['popper']}>
          <Transition
            show={open}
            enter={classNames('transition ease-out duration-100', enterDelay)}
            enterFrom="transform opacity-0 scale-95"
            enterTo="transform opacity-100 scale-100"
            leave={classNames('transition ease-in duration-75', leaveDelay)}
            leaveFrom="transform opacity-100 scale-100"
            leaveTo="transform opacity-0 scale-95"
            beforeEnter={() => setPopperElement(popperElRef.current)}
            afterLeave={() => setPopperElement(null)}
          >
            <span
              className={classNames(
                'rounded bg-surface-elevated p-2 text-xs font-normal text-white shadow-md',
                messageClassName,
              )}
            >
              {message}
            </span>
          </Transition>
        </div>
      </Portal>
    </div>
  );
}

export default InfoTooltip;
